import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import "./Pricing.css";

const PricingChart = lazy(() => import("./PricingChart"));

function formatTime(totalSeconds) {
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const ss = String(totalSeconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

function formatMoney(n) {
  return Number.isFinite(n) ? n.toFixed(0) : "0";
}

function formatCompact(n) {
  if (!Number.isFinite(n)) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return `${Math.round(n)}`;
}

function clampNumber(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

const TRUST_IMAGES = [
  {
    name: "Flutterwave",
    img: "https://commons.wikimedia.org/wiki/Special:FilePath/Flutterwave%20Logo.png",
  },
  {
    name: "PCI DSS",
    img: "https://getlogovector.com/wp-content/uploads/2019/10/pci-dss-compliant-logo-vector.png",
  },
  {
    name: "KYC Verified",
    img: "https://png.pngtree.com/png-clipart/20220131/original/pngtree-kyc-mean-know-your-customer-which-is-the-process-of-authentication-png-image_7259331.png",
  },
];

function Pricing() {
  const [stage, setStage] = useState("idle");
  const [rate, setRate] = useState("1000");
  const [seconds, setSeconds] = useState(0);
  const [showChart, setShowChart] = useState(false);
  const timerRef = useRef(null);
  const chartWrapRef = useRef(null);

  const rateNum = useMemo(() => clampNumber(rate, 0, 1000000), [rate]);
  const perSecond = useMemo(() => rateNum / 60, [rateNum]);
  const totalCost = useMemo(() => seconds * perSecond, [seconds, perSecond]);

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (stage === "in_call") {
      stopTimer();
      timerRef.current = setInterval(() => {
        setSeconds((s) => s + 1);
      }, 1000);
    } else {
      stopTimer();
    }

    return () => stopTimer();
  }, [stage]);

  useEffect(() => {
    const target = chartWrapRef.current;
    if (!target || showChart) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShowChart(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [showChart]);

  const handleSimulateIncoming = () => {
    setSeconds(0);
    setStage("incoming");
  };

  const handleDecline = () => setStage("ended");

  const handleAccept = () => {
    setSeconds(0);
    setStage("in_call");
  };

  const handleEndCall = () => setStage("ended");

  const handleReset = () => {
    setSeconds(0);
    setStage("idle");
  };

  const projection = useMemo(() => {
    const ngnPerMin = 1000;
    const callsPerDay = 5;
    const minsPerCall = 10;

    const perCall = ngnPerMin * minsPerCall;
    const perDay = perCall * callsPerDay;
    const perMonth = perDay * 30;

    const data = Array.from({ length: 30 }, (_, i) => {
      const day = i + 1;
      return { day, earnings: perDay * day };
    });

    return { ngnPerMin, callsPerDay, minsPerCall, perCall, perDay, perMonth, data };
  }, []);

  return (
    <section className="pricing" id="pricing">
      <div className="pricing__header">
        <h1>
          See How <span className="highlight">Call Rates</span> Work
        </h1>
        <p>
          Set your rate per minute, preview an incoming call, and see earnings grow live
          during a call.
        </p>
      </div>

      <div className="rateDemo">
        <div className="rateDemo__panel">
          <div className="rateDemo__card">
            <div className="rateDemo__cardTop">
              <div>
                <h2 className="rateDemo__title">Set your rate</h2>
                <p className="rateDemo__hint">
                  Start with a rate that matches your value. You can adjust anytime.
                </p>
              </div>

              <div className="rateDemo__miniChip" aria-label="Per second cost">
                <span>Per sec</span>
                <b>{formatMoney(perSecond)} NGN</b>
              </div>
            </div>

            <div className="rateDemo__inputRow">
              <label className="rateDemo__label" htmlFor="rateInput">
                Rate (NGN per minute)
              </label>

              <div className="rateDemo__inputWrap">
                <input
                  id="rateInput"
                  className="rateDemo__input"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="1000000"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                />
                <span className="rateDemo__suffix">/ min</span>
              </div>
            </div>

            <div className="rateDemo__actions">
              <button className="btn btnPrimary" onClick={handleSimulateIncoming} type="button">
                Simulate Incoming Call
              </button>

              <button className="btn btnGhost" onClick={handleReset} type="button">
                Reset
              </button>
            </div>

            <div className="rateDemo__steps">
              <div className={`step ${stage === "idle" ? "active" : ""}`}>
                <span className="dot" />
                <span>Set your rate</span>
              </div>
              <div className={`step ${stage === "incoming" ? "active" : ""}`}>
                <span className="dot" />
                <span>Incoming call</span>
              </div>
              <div className={`step ${stage === "in_call" ? "active" : ""}`}>
                <span className="dot" />
                <span>In call (timer + cost)</span>
              </div>
              <div className={`step ${stage === "ended" ? "active" : ""}`}>
                <span className="dot" />
                <span>Call ended</span>
              </div>
            </div>
          </div>

          <div className="rateDemo__tips">
            <h3>Quick tips</h3>
            <ul>
              <li>Decline calls you don’t want to take.</li>
              <li>When you accept, the timer starts and cost updates live.</li>
              <li>This is a demo preview of the call experience.</li>
            </ul>
          </div>
        </div>

        <div className="rateDemo__phoneWrap">
          <div className="phone">
            <div className="phone__top">
              <div className="phone__pill" />
            </div>

            <div className="phone__screen">
              <div className="phone__status">
                <span>OKIRI</span>
                <span className="phone__statusRight">
                  <span className="signal" />
                  <span className="battery" />
                </span>
              </div>

              {stage === "idle" && (
                <div className="phone__content">
                  <p className="phone__title">Ready to preview</p>
                  <p className="phone__sub">
                    Set your rate, then tap <b>Simulate Incoming Call</b>.
                  </p>

                  <div className="phone__rateBadge">
                    <span>Your rate</span>
                    <strong>{formatMoney(rateNum)} NGN / min</strong>
                  </div>

                  <div className="phone__empty">
                    <span className="phone__emptyIcon">📞</span>
                    <p>Waiting for a call…</p>
                  </div>
                </div>
              )}

              {stage === "incoming" && (
                <div className="phone__content phone__incoming">
                  <p className="phone__incomingLabel">Incoming call</p>
                  <p className="phone__callerName">Okiri User</p>
                  <p className="phone__callerMeta">
                    Paid call • {formatMoney(rateNum)} NGN / min
                  </p>

                  <div className="phone__incomingActions">
                    <button className="callBtn decline" onClick={handleDecline} type="button">
                      Decline
                    </button>
                    <button className="callBtn accept" onClick={handleAccept} type="button">
                      Accept
                    </button>
                  </div>

                  <p className="phone__hint">Tip: Accept to start the timer and see live cost.</p>
                </div>
              )}

              {stage === "in_call" && (
                <div className="phone__content phone__incall">
                  <p className="phone__incallLabel">In call</p>
                  <p className="phone__callerName">Okiri User</p>

                  <div className="phone__stats">
                    <div className="stat">
                      <span className="stat__label">Time</span>
                      <span className="stat__value">{formatTime(seconds)}</span>
                    </div>

                    <div className="stat">
                      <span className="stat__label">Rate</span>
                      <span className="stat__value">{formatMoney(rateNum)} / min</span>
                    </div>

                    <div className="stat stat--wide">
                      <span className="stat__label">Live cost</span>
                      <span className="stat__value">{formatMoney(totalCost)} NGN</span>
                    </div>
                  </div>

                  <div className="phone__incallActions">
                    <button className="callBtn end" onClick={handleEndCall} type="button">
                      End call
                    </button>
                  </div>

                  <p className="phone__hint">Tip: Your earnings update as the call continues.</p>
                </div>
              )}

              {stage === "ended" && (
                <div className="phone__content phone__ended">
                  <p className="phone__title">Call ended</p>
                  <p className="phone__sub">
                    Duration: <b>{formatTime(seconds)}</b>
                    <br />
                    Total cost: <b>{formatMoney(totalCost)} NGN</b>
                  </p>

                  <button className="btn btnPrimary full" onClick={handleReset} type="button">
                    Start again
                  </button>
                </div>
              )}
            </div>

            <div className="phone__bottom">
              <div className="phone__homeBar" />
            </div>
          </div>

          <p className="rateDemo__belowNote">Pricing updates in real-time while you’re on a call.</p>
        </div>
      </div>

      <section className="earnings" aria-label="Earnings projection">
        <div className="earnings__inner">
          <div className="earnings__left">
            <h2 className="earnings__title">What Could You Earn?</h2>

            <div className="earnings__calc">
              <div className="calcRow">
                <span>₦{projection.ngnPerMin.toLocaleString()}/min</span>
                <span className="calcX">×</span>
                <span>{projection.callsPerDay} calls/day</span>
                <span className="calcX">×</span>
                <span>{projection.minsPerCall} mins/call</span>
              </div>

              <div className="calcResult">
                <div className="calcBig">
                  <div>
                    <span className="calcLabel">= per day</span>
                    <b>₦{projection.perDay.toLocaleString()}</b>
                  </div>
                  <div className="calcDivider" />
                  <div>
                    <span className="calcLabel">= per month</span>
                    <b>₦{projection.perMonth.toLocaleString()}</b>
                  </div>
                </div>

                <p className="earnings__note">
                  This is a simple example to help you visualize the income potential.
                </p>
              </div>
            </div>
          </div>

          <div className="earnings__right">
            <div className="earnings__chartCard">
              <div className="earnings__chartTop">
                <div>
                  <p className="earnings__chartTitle">Monthly Earnings Trend</p>
                  <p className="earnings__chartSub">Cumulative over 30 days (₦50k/day)</p>
                </div>
                <div className="earnings__badge">
                  Target: <b>₦{formatCompact(projection.perMonth)}</b>
                </div>
              </div>

              <div className="earnings__chartWrap" ref={chartWrapRef}>
                {showChart ? (
                  <Suspense fallback={<div className="chartFallback">Loading chart...</div>}>
                    <PricingChart data={projection.data} />
                  </Suspense>
                ) : (
                  <div className="chartFallback" aria-hidden="true">Loading chart...</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="trustStatic" aria-label="Payments & security">
        <div className="trustStatic__inner">
          <h3 className="trustStatic__title">Payments & Verification</h3>

          <div className="trustStatic__row">
            {TRUST_IMAGES.map((item) => (
              <div className="trustStatic__item" key={item.name} title={item.name}>
                <img
                  src={item.img}
                  alt={item.name}
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <span>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </section>
  );
}

export default Pricing;